import { IHeader } from '@/bundles/BaseTable/interfaces';

/**
 * Maps a Vue 2 shaped `{ text, value }` header onto Vuetify 3's.
 *
 * `title`: Vuetify 3 reads the label from `title` and has no `text` fallback, so it is copied.
 *
 * `key`: Vuetify 3 only makes a column sortable when the RAW header has a `key`
 * (`sortable ?? (key != null || !!sort)`, before its own `key ?? value` fallback), while
 * Vuetify 2 sorted every column unless it said `sortable: false`. Without `key` the filter sets,
 * tasks and contacts tables stopped sorting on a header click. `value` becomes the `key` when it
 * names a field; an explicit `sortable: false` still wins, and an empty `value` (the tasks
 * table's `Related To`) stays unkeyed, so it does not sort on `''`.
 *
 * `text`/`value` are kept because `BaseTable`'s own `#header.multi` slot reads them.
 *
 * The action columns (`data-table-select`, `data-table-expand`) need `key` so Vuetify does not
 * inject a second selection column (`extractKeys` reads the raw `key` only); Vuetify's own
 * action header keeps them unsortable.
 *
 * Vuetify 2 also put a header's `class` on its `<th>`; Vuetify 3 takes `<th>` attributes from
 * `headerProps` instead, so `class` is carried over (`text-center`, the `white-space--pre-line`
 * that breaks "Created\n Updated" onto two lines).
 */
export const withHeaderTitles = (headers: IHeader[]): IHeader[] => headers.map((header) => ({
  title: header.text,
  ...(typeof header.value === 'string' && header.value ? { key: header.value } : {}),
  ...header,
  ...(header.class ? { headerProps: { class: header.class, ...header.headerProps } } : {}),
  ...(header.multi ? { multi: withHeaderTitles(header.multi) } : {}),
}));
